import React, { useState } from "react";
import {
    Button, Box, SpaceBetween, Modal, Cards, FormField, Header
} from "@amzn/awsui-components-react/polaris";
import PromptInput from "@amzn/awsui-components-console/prompt-input";
import { FremontExpandableSection } from "../../utils/CommonComponents";
import workflows from "./workflowDefinitions";

const WorkflowSelector = ({
    visible,
    onDismiss,
    onApplyWorkflow
}) => {
    const [selectedWorkflow, setSelectedWorkflow] = useState(null);
    const [workflowInputs, setWorkflowInputs] = useState({});

    // Handle workflow selection
    const handleWorkflowSelect = (workflow) => {
        setSelectedWorkflow(workflow);
        // Initialize inputs for the selected workflow
        const inputs = {};
        workflow.requiredUserInputs.forEach((input) => {
            inputs[input.name] = "";
        });
        setWorkflowInputs(inputs);
    };

    // Handle modal dismissal with cleanup
    const handleDismiss = () => {
        setSelectedWorkflow(null);
        setWorkflowInputs({});
        onDismiss();
    };

    // Handle input changes in workflow modal
    const handleWorkflowInputChange = (inputName, value) => {
        setWorkflowInputs(prev => ({
            ...prev,
            [inputName]: value
        }));
    };

    // Apply workflow template to chat input
    const handleApplyWorkflow = () => {
        if (!selectedWorkflow) return;

        let { prompt } = selectedWorkflow;

        // Replace template variables with user inputs
        Object.entries(workflowInputs).forEach(([key, value]) => {
            const template = `{{${key}}}`;
            prompt = prompt.replace(new RegExp(template, "g"), value);
        });

        // Pass the populated prompt to parent component
        onApplyWorkflow(prompt);

        // Reset state
        handleDismiss();
    };

    // Check if all required inputs are filled
    const isWorkflowComplete = selectedWorkflow &&
        selectedWorkflow?.requiredUserInputs?.every(input =>
            workflowInputs[input.name] && workflowInputs[input.name].trim());

    return (
        <Modal
            onDismiss={handleDismiss}
            visible={visible}
            header="Select Workflow Template"
            size="large"
            footer={
                selectedWorkflow && (
                    <Box float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button
                                variant="link"
                                onClick={handleDismiss}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                disabled={!isWorkflowComplete}
                                onClick={handleApplyWorkflow}
                            >
                                Apply to Chat
                            </Button>
                        </SpaceBetween>
                    </Box>
                )
            }
        >
            <SpaceBetween size="l">
                {!selectedWorkflow ? (
                    <Cards
                        cardDefinition={{
                            header: item => item.name,
                            sections: [
                                {
                                    content: item => item.description
                                }
                            ]
                        }}
                        cardsPerRow={[
                            { cards: 1 },
                            { minWidth: 500, cards: 2 }
                        ]}
                        items={workflows}
                        selectionType="single"
                        onSelectionChange={({ detail }) => {
                            if (detail.selectedItems.length > 0) {
                                handleWorkflowSelect(detail.selectedItems[0]);
                            }
                        }}
                        empty={
                            <Box textAlign="center" color="inherit">
                                <Box variant="strong" color="inherit">
                                    No workflows available
                                </Box>
                                <Box padding={{ top: "s" }} variant="p" color="inherit">
                                    Contact your administrator to add workflow templates.
                                </Box>
                            </Box>
                        }
                        header={
                            <Header variant="h3">
                                Choose a workflow template to get started
                            </Header>
                        }
                    />
                ) : (
                    <SpaceBetween size="l">
                        <Box>
                            <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                                <Button
                                    variant="icon"
                                    iconName="arrow-left"
                                    onClick={() => {
                                        setSelectedWorkflow(null);
                                        setWorkflowInputs({});
                                    }}
                                    ariaLabel="Back to workflow list"
                                />
                                <Box variant="h3">
                                    {selectedWorkflow.name}
                                </Box>
                            </SpaceBetween>
                            <Box margin={{ bottom: "l", left: "xl" }}>
                                {selectedWorkflow.description}
                            </Box>
                        </Box>

                        <SpaceBetween size="m">
                            <Header variant="h4">
                                Fill in the required information
                            </Header>
                            {selectedWorkflow.requiredUserInputs.map(input => (
                                <FormField
                                    key={input.name}
                                    label={input.label}
                                >
                                    <PromptInput
                                        value={workflowInputs[input.name] || ""}
                                        onChange={({ detail }) =>
                                            handleWorkflowInputChange(input.name, detail.value)
                                        }
                                        placeholder={input.placeholder}
                                        maxRows={5}
                                    />
                                </FormField>
                            ))}
                        </SpaceBetween>

                        <FremontExpandableSection
                            header="Preview"
                            defaultExpanded={false}
                            variant="footer"
                        >
                            <Box
                                padding="s"
                                backgroundColor="background-container-content"
                                color="text-body-secondary"
                            >
                                {(() => {
                                    let preview = selectedWorkflow.prompt;
                                    Object.entries(workflowInputs).forEach(([key, value]) => {
                                        const template = `{{${key}}}`;
                                        preview = preview.replace(
                                            new RegExp(template, "g"),
                                            value || `[${key}]`
                                        );
                                    });
                                    return preview;
                                })()}
                            </Box>
                        </FremontExpandableSection>
                    </SpaceBetween>
                )}
            </SpaceBetween>
        </Modal>
    );
};

export default WorkflowSelector;
